import { supabase, supabaseAnon, isSupabaseAvailable } from '../lib/supabaseClient'
import { buildCardImgs, buildCardVideoUrl } from '../utils/mediaMappers'
import { track } from '../utils/track'

// ── Edge function: admin-listings ─────────────────────────────────────────────
// All writes (create, update, delete) go through this edge function.
// Uses supabase.functions.invoke() which auto-attaches the session JWT.
// Hard timeout for a single edge-function call. Supabase edge functions can
// cold-start slowly on free-tier projects; without a ceiling the UI hangs
// indefinitely because supabase.functions.invoke() has no built-in timeout.
// 30 s is enough for a warm instance (responds in 1-3 s) and for a cold
// Deno start (typically 10-20 s). The auto-retry in callListingsEdge handles
// the rare case where the first attempt hits the very start of a cold boot.
const EDGE_TIMEOUT_MS = 20_000

// Hard ceiling for a direct DB write (insert/update). supabase-js attaches the
// auth token to every PostgREST request and silently triggers a token refresh
// when the cached token is stale; if that refresh hangs, the write never
// settles and the UI sits on the 90s save-ceiling with no real error. A real
// AbortSignal makes the write fail fast with a message the user can act on.
const WRITE_TIMEOUT_MS = 20_000
const READBACK_TIMEOUT_MS = 10_000

/** A clear, actionable error for a write that aborted (almost always a stale session). */
function writeTimeoutError(): Error {
  return new Error(
    'Save timed out reaching the database. Your admin session may have expired, ' +
    'please log out and back in, then try again. Your changes are still on screen.'
  )
}

/**
 * Proactively obtain a valid session BEFORE a write so the write itself never
 * stalls on an inline token refresh (the classic "idle for a while, come back,
 * save → hang" case: the access token expired while idle, and supabase-js then
 * refreshes it inline when the write attaches the JWT). getSession() refreshes
 * an expired token through the (now-serialized) auth lock; we time-box it so a
 * genuinely dead connection fails fast with a clear message instead of hanging.
 * Returns when a session is confirmed; throws an actionable error otherwise.
 */
async function ensureSessionReady(maxMs = 6000): Promise<void> {
  if (!supabase) return
  let session: unknown = null
  try {
    const result: any = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('session timeout')), maxMs)),
    ])
    session = result?.data?.session ?? null
  } catch {
    session = null
  }
  if (!session) {
    throw new Error(
      'Your admin session has expired or could not be refreshed. Please log out and ' +
      'back in, then save again. Your changes are still on screen.'
    )
  }
}

async function callListingsEdgeOnce(payload: Record<string, unknown>): Promise<any> {
  if (!supabase) throw new Error('Supabase not configured, cannot reach admin-listings edge function')

  const invokePromise = supabase.functions.invoke('admin-listings', { body: payload })
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error('__TIMEOUT__')),
      EDGE_TIMEOUT_MS,
    )
  )

  const { data, error } = await Promise.race([invokePromise, timeoutPromise])
  if (error) {
    // FunctionsHttpError.context is the raw Response object. Read the JSON body to
    // extract the actual Postgres/edge-function error text so the column-retry regex
    // can match "column X does not exist" and strip the offending field.
    const context = (error as any).context
    if (context && typeof context.json === 'function') {
      let body: any
      try { body = await context.json() } catch { /* ignore parse failure */ }
      if (body?.error) throw new Error(body.error)
      if (body?.message) throw new Error(body.message)
    }
    throw new Error((error as any).message || String(error) || 'Edge function error')
  }
  if (!data?.success) throw new Error(data?.error || 'Edge function error')
  return data.data
}

// Wraps callListingsEdgeOnce with one automatic retry on timeout.
// The first call often hits a Deno cold-start; the retry hits the warm instance.
async function callListingsEdge(payload: Record<string, unknown>): Promise<any> {
  try {
    return await callListingsEdgeOnce(payload)
  } catch (e) {
    if (e instanceof Error && e.message === '__TIMEOUT__') {
      console.warn('[listings] Edge function timed out, retrying once on warm instance…')
      try {
        return await callListingsEdgeOnce(payload)
      } catch (e2) {
        if (e2 instanceof Error && e2.message === '__TIMEOUT__') {
          throw new Error('Save timed out, the server did not respond after two attempts. Check your connection and try again.')
        }
        throw e2
      }
    }
    throw e
  }
}

// Detect a "column X does not exist" error and return the offending column name.
// Postgres surfaces these as either:
//   "column \"foo\" of relation \"listings\" does not exist"
//   "Could not find the 'foo' column of 'listings' in the schema cache"
// PostgREST schema-cache misses look like the second form. We strip the missing
// column from the payload and retry once so a not-yet-applied migration doesn't
// brick the entire save.
function extractMissingColumn(message: string): string | null {
  if (!message) return null
  const m1 = message.match(/column "([^"]+)" of relation "[^"]+" does not exist/i)
  if (m1) return m1[1]
  const m2 = message.match(/Could not find the '([^']+)' column/i)
  if (m2) return m2[1]
  return null
}

// Run a save (create or update) against the listings edge function. If the
// payload references columns that don't exist in the live DB yet (typically
// when a migration hasn't been applied), strip those columns and retry up to
// 5 times. This keeps the save flow alive while surfacing a console warning
// so we know the schema is drifting.
async function callListingsEdgeWithColumnRetry(
  payload: { action: string; id?: string; payload: Record<string, unknown> },
): Promise<any> {
  const stripped: string[] = []
  let body = { ...payload, payload: { ...payload.payload } }
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      return await callListingsEdge(body)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      const missing = extractMissingColumn(msg)
      if (!missing || !(missing in body.payload)) throw e
      console.warn(
        `[listings] Column "${missing}" not present in DB, stripping and retrying. ` +
        `Apply the matching migration to persist this field.`,
      )
      const next = { ...body.payload }
      delete next[missing]
      body = { ...body, payload: next }
      stripped.push(missing)
    }
  }
  throw new Error(
    `Listings save failed after stripping columns: ${stripped.join(', ')}. ` +
    `Schema cache may be stale, apply pending migrations.`,
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert camelCase keys to snake_case for database storage
 * Handles acronyms properly (e.g., heroCTA -> hero_cta, not hero_c_t_a)
 */
function camelToSnake(obj: any): any {
  const result: any = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Convert camelCase to snake_case, handling acronyms
      // heroCTA -> hero_cta (not hero_c_t_a)
      // heroVideoUrl -> hero_video_url
      const snakeKey = key
        .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert _ before uppercase after lowercase
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2') // Handle acronyms like URLParser -> URL_Parser
        .toLowerCase()

      result[snakeKey] = obj[key]
    }
  }

  return result
}

/**
 * Convert snake_case keys back to camelCase for frontend use
 * Database returns snake_case, but frontend expects camelCase
 */
function snakeToCamel(obj: any): any {
  const result: any = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Convert snake_case to camelCase
      // hero_cta -> heroCTA, hero_title -> heroTitle
      const camelKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
      result[camelKey] = obj[key]
    }
  }

  return result
}

/**
 * Central mapper: Converts raw Supabase listing (snake_case) to frontend format (camelCase)
 *
 * This is the single point where all snake_case → camelCase conversion happens.
 * All UI components consume camelCase only. No dual access patterns in components.
 *
 * @param rawListing - Raw snake_case data from Supabase
 * @returns Clean camelCase Listing object for UI consumption
 */
export function mapListingFromDb(rawListing: any): Listing {
  if (!rawListing) return {} as Listing;

  // Convert all fields from snake_case to camelCase
  const listing = snakeToCamel(rawListing) as Listing;

  // Normalize media URLs: prepend "/" to relative paths for public folder access
  if (Array.isArray(listing.mediaItems)) {
    listing.mediaItems = listing.mediaItems.map((item: any) => ({
      ...item,
      url: item.url ? normaliseUrl(item.url) : item.url,
      src: item.src ? normaliseUrl(item.src) : item.src,
    }));
  }

  // Build imgs array from media_items for display; filter out items with no resolved URL
  if (Array.isArray(listing.mediaItems) && !listing.imgs) {
    listing.imgs = buildCardImgs(listing.mediaItems).filter((img: any) => img.src);
  }

  // Fallback: intake listings pushed before media_items fix have hero_images but
  // no media_items JSONB. Build a minimal imgs array from heroImages so QV + cards
  // always have at least a featured image to display.
  if ((!listing.imgs || listing.imgs.length === 0) && Array.isArray(listing.heroImages) && listing.heroImages.length > 0) {
    listing.imgs = listing.heroImages
      .filter((h: any) => h?.url && isValidImageUrl(h.url))   // skip bare bucket paths
      .map((h: any, i: number) => ({
        src:        normaliseUrl(h.url),
        url:        normaliseUrl(h.url),
        alt_text:   '',
        is_featured: h.featured ?? i === 0,
        sort_order:  i,
      }));
  }

  // Build video URL from media_items
  if (Array.isArray(listing.mediaItems) && !listing.videoUrl) {
    listing.videoUrl = buildCardVideoUrl(listing.mediaItems) ?? undefined;
  }

  // Fallback: pull video URL from card_settings JSONB if no video from media_items
  if (!listing.videoUrl && listing.cardSettings && typeof listing.cardSettings === 'object') {
    const cs = listing.cardSettings as Record<string, any>;
    for (const type of ['venue', 'vendor', 'gcard']) {
      const entry = cs[type];
      if (!entry) continue;
      const mt = entry.media_type;
      // YouTube / Vimeo → use media_url embed link
      if (entry.media_url && (mt === 'youtube' || mt === 'vimeo')) {
        listing.videoUrl = entry.media_url;
        break;
      }
      // Native video files → first non-empty from video_urls
      if (mt === 'video' && Array.isArray(entry.video_urls)) {
        const url = entry.video_urls.find((u: string) => u && u.trim());
        if (url) { listing.videoUrl = url.trim(); break; }
      }
      // Reel / story videos → first non-empty from reel_urls
      if (mt === 'reel' && Array.isArray(entry.reel_urls)) {
        const url = entry.reel_urls.find((u: string) => u && u.trim());
        if (url) { listing.videoUrl = url.trim(); break; }
      }
    }
  }

  return listing;
}

/**
 * Normalize URL: prepend "/" to relative paths for public folder access
 * External URLs (http, blob, data) pass through unchanged
 */
function normaliseUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('/') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  return '/' + url;
}

/**
 * Validates that an image URL points to an actual file, not a bare bucket path.
 * Filters out entries like "https://project.supabase.co/storage/v1/object/public/bucket/"
 * which are truthy strings but render as broken images.
 */
function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const path = url.split('?')[0].trim();
  if (path.endsWith('/')) return false;
  const filename = path.split('/').pop() || '';
  return filename.length > 0;
}

/**
 * Calculate content quality score (0-100) based on editorial completeness
 * Score breakdown:
 * - Section intros completed: 0-40 points (N filled sections / 6 max)
 * - Fact-checked: 30 points
 * - Approved: 30 points
 */
export function calculateContentQualityScore(
  sectionIntros: Record<string, string> | undefined,
  factChecked: boolean,
  approved: boolean
): number {
  let score = 0;

  // Section intros: 0-40 points (6 sections max)
  if (sectionIntros && typeof sectionIntros === 'object') {
    const filledSections = Object.values(sectionIntros)
      .filter(intro => intro && typeof intro === 'string' && intro.trim().length > 0)
      .length;
    const maxSections = Object.keys(sectionIntros).length || 6;
    score += Math.round((filledSections / maxSections) * 40);
  }

  // Fact-checked: 30 points
  if (factChecked === true) score += 30;

  // Approved: 30 points
  if (approved === true) score += 30;

  return Math.min(100, Math.max(0, score));
}

/**
 * Quality tier definitions for editorial curation
 * Tiers are assigned based on content_quality_score (0-100)
 */
export const QUALITY_TIERS = {
  platinum: {
    score: [90, 100],
    label: 'Platinum',
    icon: '◆',
    color: '#D4AF37',
    bgColor: 'rgba(212, 175, 55, 0.1)',
    description: 'Exceptional venue with comprehensive, reviewed content'
  },
  signature: {
    score: [70, 89],
    label: 'Signature',
    icon: '★',
    color: '#d39a21',
    bgColor: 'rgba(201, 168, 76, 0.1)',
    description: 'Premium venue with strong editorial standards'
  },
  approved: {
    score: [50, 69],
    label: 'Approved',
    icon: '✓',
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    description: 'Verified and approved venue'
  },
  standard: {
    score: [0, 49],
    label: 'Standard',
    icon: null,
    color: '#9ca3af',
    bgColor: 'transparent',
    description: 'Standard listing'
  }
};

/**
 * Get quality tier based on content quality score
 * Maps 0-100 score to tier string: platinum, signature, approved, or standard
 */
export function getQualityTier(contentQualityScore: number): 'platinum' | 'signature' | 'approved' | 'standard' {
  if (contentQualityScore >= 90) return 'platinum';
  if (contentQualityScore >= 70) return 'signature';
  if (contentQualityScore >= 50) return 'approved';
  return 'standard';
}

/**
 * Get tier properties (label, icon, color, etc.) by tier name
 * Returns the tier configuration object with styling and metadata
 */
export function getTierProperties(tier: string): any {
  return QUALITY_TIERS[tier as keyof typeof QUALITY_TIERS] || QUALITY_TIERS.standard;
}

/**
 * Transform Supabase listing data to match frontend UI expectations
 * Maps database field names and values to expected format
 */
function transformSupabaseListingForUI(listing: any): any {
  const transformed = { ...listing }

  // Map status: draft -> Draft, published -> Published, paused -> Paused, archived -> Archived
  if (transformed.status) {
    transformed.status = transformed.status.charAt(0).toUpperCase() + transformed.status.slice(1)
  }

  // Ensure category field exists (use categorySlug if needed)
  if (!transformed.category && transformed.categorySlug) {
    transformed.category = transformed.categorySlug
  }

  // Set default values for display fields if missing
  if (!transformed.listed) {
    transformed.listed = transformed.createdAt ? new Date(transformed.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'
  }
  if (!transformed.lastUpdated) {
    transformed.lastUpdated = transformed.updatedAt ? new Date(transformed.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'
  }
  if (!transformed.expires) {
    transformed.expires = 'N/A'
  }

  // Build rich imgs[] and primary videoUrl from media_items.
  // Any component rendering LuxuryVenueCard / LuxuryVendorCard from a DB listing will
  // receive structured image objects instead of plain URL strings, and the first
  // public video item's URL will populate the card's autoplay video slot.
  if (Array.isArray(transformed.mediaItems)) {
    if (!transformed.imgs) {
      transformed.imgs = buildCardImgs(transformed.mediaItems).filter((img: any) => img.src)
    }
    if (!transformed.videoUrl) {
      transformed.videoUrl = buildCardVideoUrl(transformed.mediaItems) ?? undefined
    }
  }

  // Fallback: hero_images → imgs for intake listings without media_items
  if ((!transformed.imgs || transformed.imgs.length === 0) && Array.isArray(transformed.heroImages) && transformed.heroImages.length > 0) {
    transformed.imgs = transformed.heroImages
      .filter((h: any) => h?.url && isValidImageUrl(h.url))   // skip bare bucket paths
      .map((h: any, i: number) => ({
        src:         h.url,
        url:         h.url,
        alt_text:    '',
        is_featured: h.featured ?? i === 0,
        sort_order:  i,
      }));
  }

  // Format priceFrom with currency symbol.
  // DB stores price_from (number) + price_currency (symbol like "£" or code like "GBP").
  // Cards expect a formatted string like "£18,000". Build it here so every consumer gets it.
  if (transformed.priceFrom != null) {
    const raw = String(transformed.priceFrom).trim()
    // Only format if it looks like a plain number (no symbol already present)
    if (/^\d[\d,.]*$/.test(raw)) {
      const num = Number(raw.replace(/,/g, ''))
      if (!isNaN(num) && num > 0) {
        // Resolve currency: could be a symbol ("£") or code ("GBP") or empty
        const cur = (transformed.priceCurrency || '').trim()
        const codeToSymbol: Record<string, string> = {
          GBP: '£', EUR: '€', USD: '$', CHF: 'CHF ', AED: 'AED ', AUD: 'A$', CAD: 'C$',
          INR: '₹', JPY: '¥', CNY: '¥', SEK: 'kr ', NOK: 'kr ', DKK: 'kr ', ZAR: 'R',
        }
        // If it's already a symbol (£, €, $) use it directly; if it's a code, map it
        const sym = cur.length <= 3 && codeToSymbol[cur.toUpperCase()]
          ? codeToSymbol[cur.toUpperCase()]
          : (cur || '£')
        transformed.priceFrom = `${sym}${num.toLocaleString('en-GB')}`
      }
    }
    // Per-person pricing: when price_label flags it, suffix the figure with "pp"
    // so "£375" reads as "£375pp" (not a total). Non-per-person labels are ignored.
    const unit = String(transformed.priceLabel || '').trim().toLowerCase()
    if (/^(pp|per ?person|per ?head|per ?guest|pppn)$/.test(unit) && typeof transformed.priceFrom === 'string' && !/pp$/.test(transformed.priceFrom)) {
      transformed.priceFrom = `${transformed.priceFrom}pp`
    }
  }

  // Build showcaseUrl so cards + QuickViewModal can link to the showcase page
  if (!transformed.showcaseUrl && transformed.showcaseEnabled && transformed.slug) {
    transformed.showcaseUrl = `/showcase/${transformed.slug}`
  }

  // Alias contentQualityScore → contentScore so LuxuryVenueCard (which reads v.contentScore) works
  if (transformed.contentQualityScore != null && transformed.contentScore == null) {
    transformed.contentScore = transformed.contentQualityScore
  }

  // Fallback: pull video URL from card_settings JSONB if no video from media_items
  if (!transformed.videoUrl && transformed.cardSettings && typeof transformed.cardSettings === 'object') {
    const cs = transformed.cardSettings as Record<string, any>;
    for (const type of ['venue', 'vendor', 'gcard']) {
      const entry = cs[type];
      if (!entry) continue;
      const mt = entry.media_type;
      // YouTube / Vimeo → use media_url embed link
      if (entry.media_url && (mt === 'youtube' || mt === 'vimeo')) {
        transformed.videoUrl = entry.media_url;
        break;
      }
      // Native video files → first non-empty from video_urls
      if (mt === 'video' && Array.isArray(entry.video_urls)) {
        const url = entry.video_urls.find((u: string) => u && u.trim());
        if (url) { transformed.videoUrl = url.trim(); break; }
      }
      // Reel / story videos → first non-empty from reel_urls
      if (mt === 'reel' && Array.isArray(entry.reel_urls)) {
        const url = entry.reel_urls.find((u: string) => u && u.trim());
        if (url) { transformed.videoUrl = url.trim(); break; }
      }
    }
  }

  return transformed
}

/**
 * Normalize HTML content to ensure LTR text direction
 * Removes RTL marks and dir="rtl" attributes, ensures proper text direction
 */
function normalizeHTMLContent(html: string): string {
  if (!html || typeof html !== 'string') return html

  // Remove RTL direction attributes from HTML tags
  let normalized = html
    .replace(/\s+dir=["']?rtl["']?/gi, '') // Remove dir="rtl" or dir='rtl' attributes
    .replace(/\s+dir=["']?ltr["']?/gi, '') // Remove dir="ltr" attributes (we'll add explicit ltr styling)

  // Remove RTL Unicode characters (right-to-left marks, etc.)
  // U+200F = RIGHT-TO-LEFT MARK
  // U+202E = RIGHT-TO-LEFT OVERRIDE
  // U+202D = LEFT-TO-RIGHT OVERRIDE
  // U+061C = ARABIC LETTER MARK
  normalized = normalized
    .replace(/\u200F/g, '') // RLM
    .replace(/\u202E/g, '') // RLO
    .replace(/\u202D/g, '') // LRO
    .replace(/\u061C/g, '') // ALM

  // Add LTR styling to HTML and body tags if they exist
  // Ensure all content containers have LTR text direction
  if (normalized.includes('<div') || normalized.includes('<p') || normalized.includes('<span')) {
    // Content already has HTML structure, ensure top-level direction is set
    // This will be handled by the component's inline styles
  }

  return normalized
}

/**
 * Generate a URL-friendly slug from text
 */
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

/**
 * Prepare listing data for database storage
 * Normalizes HTML content and ensures text direction is LTR
 */
function prepareListingData(data: Listing): Listing {
  const prepared = { ...data }

  // Normalize HTML content fields to remove RTL marks and ensure LTR text direction
  const htmlFields = [
    'description',
    'shortDescription',
    'heroTitle',
    'heroSubtitle',
    'heroDescription',
    'heroButton',
    'cardTitle',
    'cardSubtitle',
    'cardSummary',
    'internalNotes',
    'seoTitle',
    'seoDescription',
    'seoKeywords'
  ]

  for (const field of htmlFields) {
    if ((prepared as any)[field] && typeof (prepared as any)[field] === 'string') {
      (prepared as any)[field] = normalizeHTMLContent((prepared as any)[field])
    }
  }

  // Auto-generate slug if not provided or empty
  if (!prepared.slug || prepared.slug.trim() === '') {
    prepared.slug = generateSlug(prepared.name || 'listing')
  }

  // Ensure all required fields are present
  if (!prepared.listingType) prepared.listingType = 'venue'
  if (!prepared.tier) prepared.tier = 'platinum'

  return prepared
}

/**
 * Map form field names to database field names
 * Complete explicit mapping to ensure no conversion errors
 */
function mapFormToDatabaseFields(data: any): any {
  const mapped: any = {}

  // Complete field mapping from form (camelCase) to database (snake_case)
  const fieldMap: { [key: string]: string } = {
    // Core listing data
    'listingType': 'listing_type',
    'tier': 'tier',
    // name (form) → venue_name (DB column): public page reads venue_name,
    // the legacy 'name' DB column is kept untouched here to avoid double-writing.
    'name': 'venue_name',
    'heroTagline': 'hero_tagline',
    'slug': 'slug',
    'description': 'description',
    'shortDescription': 'short_description',
    'country': 'country',
    'countrySlug': 'country_slug',
    'region': 'region',
    'regionSlug': 'region_slug',
    'city': 'city',
    'citySlug': 'city_slug',
    'address': 'address',
    'addressLine2': 'address_line2',
    'postcode': 'postcode',
    'lat': 'lat',
    'lng': 'lng',
    'nearestAirport': 'nearest_airport',
    'travelTime': 'travel_time',
    'categorySlug': 'category_slug',
    'category': 'category',            // keep legacy top-level category in sync with category_slug
    'subCategorySlug': 'sub_category_slug',
    'assignedCategories': 'assigned_categories',
    'tags': 'tags',
    'styles': 'styles',
    'specialties': 'specialties',
    'serviceTier': 'service_tier',
    'cardTitle': 'card_title',
    'cardSubtitle': 'card_subtitle',
    'cardSummary': 'card_summary',
    'cardImage': 'card_image',
    'cardBadge': 'card_badge',
    'cardStyle': 'card_style',
    'cardFeatured': 'card_featured',
    'cardPromoted': 'card_promoted',
    'heroType': 'hero_type',
    'heroLayout': 'hero_layout',
    'heroEyebrow': 'hero_eyebrow',
    'heroTitle': 'hero_title',
    'heroSubtitle': 'hero_subtitle',
    'heroImage': 'hero_image',
    'heroImages': 'hero_images',
    'heroImageSet': 'hero_image_set',
    'heroVideoUrl': 'hero_video_url',
    'heroYoutubeUrl': 'hero_youtube_url',
    'heroVimeoUrl': 'hero_vimeo_url',
    'heroPosterImage': 'hero_poster_image',
    'heroCTA': 'hero_cta',
    'heroBadge': 'hero_badge',
    'capacityMin': 'capacity_min',
    'capacityMax': 'capacity_max',
    'builtYear': 'built_year',
    'propertySize': 'property_size',
    'propertySizeUnit': 'property_size_unit',
    'rating': 'rating',
    'reviewCount': 'review_count',
    'priceLabel': 'price_label',
    'priceFrom': 'price_from',
    'priceCurrency': 'price_currency',
    'venueType': 'venue_type',
    // Exclusive Use block
    'exclusiveUseEnabled':     'exclusive_use_enabled',
    'exclusiveUseTitle':       'exclusive_use_title',
    'exclusiveUseSubtitle':    'exclusive_use_subtitle',
    'exclusiveUsePrice':       'exclusive_use_price',
    'exclusiveUseSubline':     'exclusive_use_subline',
    'exclusiveUseDescription': 'exclusive_use_description',
    'exclusiveUseCtaText':     'exclusive_use_cta_text',
    'exclusiveUseIncludes':    'exclusive_use_includes',
    // Multi-pin venues, secondary locations (max 3) stored as JSONB array
    'additionalLocations':     'additional_locations',
    // Catering cards (max 3, stored as JSONB array)
    'cateringEnabled':         'catering_enabled',
    'cateringCards':           'catering_cards',
    // Wedding Weekend day cards (max 4)
    'weddingWeekendEnabled':   'wedding_weekend_enabled',
    'weddingWeekendSubtitle':  'wedding_weekend_subtitle',
    'weddingWeekendDays':      'wedding_weekend_days',
    // On the Estate + Nearby Experiences
    'estateEnabled':           'estate_enabled',
    'estateItems':             'estate_items',
    'nearbyEnabled':           'nearby_enabled',
    'nearbyItems':             'nearby_items',
    // FAQ section
    'faqEnabled':              'faq_enabled',
    'faqTitle':                'faq_title',
    'faqSubtitle':             'faq_subtitle',
    'faqCtaEnabled':           'faq_cta_enabled',
    'faqCtaHeadline':          'faq_cta_headline',
    'faqCtaSubtext':           'faq_cta_subtext',
    'faqCtaButtonText':        'faq_cta_button_text',
    'faqCategories':           'faq_categories',
    // GEO Phase 2.5, structured FAQ for FAQPage JSON-LD schema
    'faqItems':                'faq_items',
    // GEO Phase 3, expanded bio sections for E-E-A-T depth
    'bioPhilosophy':           'bio_philosophy',
    'bioSignatureStyle':       'bio_signature_style',
    'bioEliteLogistics':       'bio_elite_logistics',
    // Wedding Packages, structured offerings (max 5) stored as JSONB array
    'weddingPackages':         'wedding_packages',
    // Venue spaces (max 5, stored as JSONB array)
    'spaces': 'spaces',
    // Rooms & accommodation
    'roomsAccommodationType': 'rooms_accommodation_type',
    'roomsTotal': 'rooms_total',
    'roomsSuites': 'rooms_suites',
    'roomsMaxGuests': 'rooms_max_guests',
    'roomsExclusiveUse': 'rooms_exclusive_use',
    'roomsMinStay': 'rooms_min_stay',
    'roomsDescription': 'rooms_description',
    // Dining
    'diningStyle': 'dining_style',
    'diningChefName': 'dining_chef_name',
    'diningInHouse': 'dining_in_house',
    'diningExternal': 'dining_external',
    'diningMenuStyles': 'dining_menu_styles',
    'diningDietary': 'dining_dietary',
    'diningDrinks': 'dining_drinks',
    'diningDescription': 'dining_description',
    'diningMenuImages': 'dining_menu_images',
    // Rooms images
    'roomsImages': 'rooms_images',
    // Hero caption / credit (from hero_images array)
    'heroCaption': 'hero_caption',
    'heroCredit': 'hero_credit',
    // Videos (JSONB array)
    'videos': 'videos',
    // Contact profile, opening hours, social proof
    'contactProfile': 'contact_profile',
    'weddingsHosted': 'weddings_hosted',
    'memberSince': 'member_since',
    'openingHoursEnabled': 'opening_hours_enabled',
    'openingHours': 'opening_hours',
    'pressFeatures': 'press_features',
    'awards': 'awards',
    'visibility': 'visibility',
    'vendorAccountId': 'vendor_account_id',
    'managedAccountId': 'managed_account_id',
    'ceremonySpaces': 'ceremony_spaces',
    'receptionSpaces': 'reception_spaces',
    'accommodation': 'accommodation',
    'dining': 'dining',
    'amenities': 'amenities',
    'exclusiveUse': 'exclusive_use',
    'indoorOption': 'indoor_option',
    'outdoorOption': 'outdoor_option',
    'fireworksPolicy': 'fireworks_policy',
    'dronePolicy': 'drone_policy',
    'accessibilityNotes': 'accessibility_notes',
    'heroMediaRole': 'hero_media_role',
    'cardMediaRole': 'card_media_role',
    'cardSettings': 'card_settings',
    'galleryMediaRole': 'gallery_media_role',
    'seoTitle': 'seo_title',
    'seoDescription': 'seo_description',
    'seoKeywords': 'seo_keywords',
    'focusKeyword': 'focus_keyword',
    'slugPreview': 'slug_preview',
    'internalNotes': 'internal_notes',
    'status': 'status',
    'isFeatured': 'is_featured',
    'isPromoted': 'is_promoted',
    'isHidden': 'is_hidden',
    'isVerified': 'is_verified',
    'showcaseEnabled': 'showcase_enabled',
    'showcaseCategory': 'showcase_category',
    'lwdScore': 'lwd_score',
    'featureStartDate': 'feature_start_date',
    'featureEndDate': 'feature_end_date',
    'enquiries': 'enquiries',
    'viewCount': 'view_count',
    'createdAt': 'created_at',
    'updatedAt': 'updated_at',
    'publishedAt': 'published_at',
    // Rich media items, stored as JSONB; File objects must be stripped before calling
    'mediaItems': 'media_items',
    // Hero images array, stored as JSONB. Separate from the singular hero_image /
    // hero_image_set columns, this is the rich-object array the Listing Studio edits.
    'heroImages': 'hero_images',
    // Description read-more toggle
    'readmoreEnabled': 'readmore_enabled',
    // Editorial Content Layer (Phase 3)
    'heroSummary': 'hero_summary',
    'sectionIntros': 'section_intros',
    'editorialApproved': 'editorial_approved',
    'editorialFactChecked': 'editorial_fact_checked',
    'editorialLastReviewedAt': 'editorial_last_reviewed_at',
    'editorialLastReviewedBy': 'editorial_last_reviewed_by',
    'refreshNotes': 'refresh_notes',
    'contentQualityScore': 'content_quality_score',
    // Vendor text sections + section photos
    'vendorStory':           'vendor_story',
    'vendorStoryTitle':      'vendor_story_title',
    'availabilityBody':      'availability_body',
    'pricingBody':           'pricing_body',
    'vendorStoryPhoto':      'vendor_story_photo',
    'vendorStoryPhotoSide':  'vendor_story_photo_side',
    'pricingPhoto':          'pricing_photo',
    'pricingPhotoSide':      'pricing_photo_side',
    'availabilityPhoto':     'availability_photo',
    'availabilityPhotoSide': 'availability_photo_side',
    // Vendor editorial content
    'specialtiesBodyP1': 'specialties_body_p1',
    'specialtiesBodyP2': 'specialties_body_p2',
    'specialtiesImages': 'specialties_images',
    'aestheticBodyP1':   'aesthetic_body_p1',
    'aestheticBodyP2':   'aesthetic_body_p2',
    'aestheticImages':   'aesthetic_images',
    // Listing Identity (taxonomy)
    'primaryArchetype':    'primary_archetype',
    'secondaryArchetypes': 'secondary_archetypes',
    'moodProfile':         'mood_profile',
    'luxuryTier':          'luxury_tier',
    'audienceFit':         'audience_fit',
    // Planner
    'plannerServices':     'planner_services',
    'plannerApproaches':   'planner_approaches',
    'plannerPricingModel': 'planner_pricing_model',
    'plannerStartingFrom': 'planner_starting_from',
    'plannerTeamSize':     'planner_team_size',
    'plannerYearsActive':  'planner_years_active',
    'plannerWeddingsYear': 'planner_weddings_year',
    'plannerDestinations': 'planner_destinations',
    'plannerPhilosophy':   'planner_philosophy',
    // Photographer / Videographer
    'photoStyles':          'photo_styles',
    'photoEmotionalTones':  'photo_emotional_tones',
    'photoCoverage':        'photo_coverage',
    'photoDeliverables':    'photo_deliverables',
    'photoTeam':            'photo_team',
    'photoStartingFrom':    'photo_starting_from',
    'photoTurnaround':      'photo_turnaround',
    'photoDestinations':    'photo_destinations',
    'photoPublications':    'photo_publications',
    'photoYearsActive':     'photo_years_active',
    'photoWeddingsYear':    'photo_weddings_year',
    'photoArtistStatement': 'photo_artist_statement',
    // Florist
    'floristStyles':             'florist_styles',
    'floristArrangements':       'florist_arrangements',
    'floristSeasons':            'florist_seasons',
    'floristConsultationTypes':  'florist_consultation_types',
    'floristSourcing':           'florist_sourcing',
    'floristTeamSize':           'florist_team_size',
    'floristYearsActive':        'florist_years_active',
    'floristStartingFrom':       'florist_starting_from',
    'floristDeliveryArea':       'florist_delivery_area',
    'floristSignatureFlowers':   'florist_signature_flowers',
    'floristPhilosophy':         'florist_philosophy',
    // Caterer
    'catererCuisineTypes':    'caterer_cuisine_types',
    'catererServiceStyles':   'caterer_service_styles',
    'catererDietary':         'caterer_dietary',
    'catererTastingOffered':  'caterer_tasting_offered',
    'catererMobileKitchen':   'caterer_mobile_kitchen',
    'catererAlcoholLicence':  'caterer_alcohol_licence',
    'catererTeamSize':        'caterer_team_size',
    'catererYearsActive':     'caterer_years_active',
    'catererStartingFrom':    'caterer_starting_from',
    'catererCredentials':     'caterer_credentials',
    'catererPhilosophy':      'caterer_philosophy',
    // Wedding Cakes
    'cakeStyles':          'cake_styles',
    'cakeFlavours':        'cake_flavours',
    'cakeDietary':         'cake_dietary',
    'cakeAlsoServes':      'cake_also_serves',
    'cakeTastingSessions': 'cake_tasting_sessions',
    'cakeCustomDesign':    'cake_custom_design',
    'cakeLeadTime':        'cake_lead_time',
    'cakeDeliveryArea':    'cake_delivery_area',
    'cakeYearsActive':     'cake_years_active',
    'cakeStartingFrom':    'cake_starting_from',
    'cakeSignatureStyle':  'cake_signature_style',
    'cakeStatement':       'cake_statement',
    // Entertainment
    'entSubTypes':        'ent_sub_types',
    'entGenres':          'ent_genres',
    'entCoverage':        'ent_coverage',
    'entLearnFirstDance': 'ent_learn_first_dance',
    'entTeamSize':        'ent_team_size',
    'entYearsActive':     'ent_years_active',
    'entStartingFrom':    'ent_starting_from',
    'entTravelRadius':    'ent_travel_radius',
    'entRider':           'ent_rider',
    'entStatement':       'ent_statement',
    // Hair & Makeup
    'hmServices':    'hm_services',
    'hmSpecialties': 'hm_specialties',
    'hmTravel':      'hm_travel',
    'hmTeamSize':    'hm_team_size',
    'hmYearsActive': 'hm_years_active',
    'hmStartingFrom':'hm_starting_from',
    'hmTrialPolicy': 'hm_trial_policy',
    'hmProducts':    'hm_products',
    'hmDestinations':'hm_destinations',
    'hmStatement':   'hm_statement',
    // Stationery
    'statStyles':          'stat_styles',
    'statPrintingMethods': 'stat_printing_methods',
    'statItems':           'stat_items',
    'statDigitalOptions':  'stat_digital_options',
    'statCustomDesign':    'stat_custom_design',
    'statLeadTime':        'stat_lead_time',
    'statYearsActive':     'stat_years_active',
    'statStartingFrom':    'stat_starting_from',
    'statStatement':       'stat_statement',
    // Bridal Wear
    'bridalServices':             'bridal_services',
    'bridalPriceRange':           'bridal_price_range',
    'bridalAppointmentRequired':  'bridal_appointment_required',
    'bridalAlterations':          'bridal_alterations',
    'bridalLeadTime':             'bridal_lead_time',
    'bridalDesigners':            'bridal_designers',
    'bridalTeamSize':             'bridal_team_size',
    'bridalYearsActive':          'bridal_years_active',
    'bridalStartingFrom':         'bridal_starting_from',
    'bridalVirtualTryon':         'bridal_virtual_tryon',
    'bridalStatement':            'bridal_statement',
    'boutiquePageLayout':         'boutique_page_layout',
    // Jeweller
    'jewelSpecialties':   'jewel_specialties',
    'jewelMetals':        'jewel_metals',
    'jewelStones':        'jewel_stones',
    'jewelCertifications':'jewel_certifications',
    'jewelCustomDesign':  'jewel_custom_design',
    'jewelYearsActive':   'jewel_years_active',
    'jewelStartingFrom':  'jewel_starting_from',
    'jewelStatement':     'jewel_statement',
    // Transport
    'transportVehicles':            'transport_vehicles',
    'transportChauffeurIncluded':   'transport_chauffeur_included',
    'transportDecorationAvailable': 'transport_decoration_available',
    'transportFleetSize':           'transport_fleet_size',
    'transportCoverageArea':        'transport_coverage_area',
    'transportYearsActive':         'transport_years_active',
    'transportStartingFrom':        'transport_starting_from',
    'transportStatement':           'transport_statement',
    // Event Design
    'edesignStyles':           'edesign_styles',
    'edesignServices':         'edesign_services',
    'edesignRental':           'edesign_rental',
    'edesignCustomFabrication':'edesign_custom_fabrication',
    'edesignTeamSize':         'edesign_team_size',
    'edesignYearsActive':      'edesign_years_active',
    'edesignStartingFrom':     'edesign_starting_from',
    'edesignSignatureLook':    'edesign_signature_look',
    'edesignStatement':        'edesign_statement',
    // Celebrant
    'celebrantCeremonyTypes': 'celebrant_ceremony_types',
    'celebrantLanguages':     'celebrant_languages',
    'celebrantTravel':        'celebrant_travel',
    'celebrantDestinations':  'celebrant_destinations',
    'celebrantYearsActive':   'celebrant_years_active',
    'celebrantWeddingsYear':  'celebrant_weddings_year',
    'celebrantStartingFrom':  'celebrant_starting_from',
    'celebrantStatement':     'celebrant_statement',
    // Event Production
    'eprodServices':    'eprod_services',
    'eprodSpecialties': 'eprod_specialties',
    'eprodTeamSize':    'eprod_team_size',
    'eprodYearsActive': 'eprod_years_active',
    'eprodStartingFrom':'eprod_starting_from',
    'eprodStatement':   'eprod_statement',
    // Health & Beauty
    'hbeautyServices':    'hbeauty_services',
    'hbeautyTravel':      'hbeauty_travel',
    'hbeautyTeamSize':    'hbeauty_team_size',
    'hbeautyYearsActive': 'hbeauty_years_active',
    'hbeautyStartingFrom':'hbeauty_starting_from',
    'hbeautyStatement':   'hbeauty_statement',
    // Accessories
    'accTypes':       'acc_types',
    'accCustom':      'acc_custom',
    'accDesigners':   'acc_designers',
    'accStartingFrom':'acc_starting_from',
    'accYearsActive': 'acc_years_active',
    'accStatement':   'acc_statement',
    // Guest Attire
    'gattireTypes':       'gattire_types',
    'gattireBespoke':     'gattire_bespoke',
    'gattireDesigners':   'gattire_designers',
    'gattireStartingFrom':'gattire_starting_from',
    'gattireYearsActive': 'gattire_years_active',
    'gattireStatement':   'gattire_statement',
    'publishVersion':     'publish_version',
  }

  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const dbKey = fieldMap[key]
      if (dbKey) {
        mapped[dbKey] = data[key]
      } else {
        // Fallback: auto-convert any camelCase key not in the explicit map.
        // Previously these were silently dropped, causing fields added to sections
        // without a corresponding fieldMap entry to never reach the database.
        // The column-retry in callListingsEdgeWithColumnRetry will safely strip
        // any key that doesn't exist as a DB column yet.
        const autoKey = key
          .replace(/([a-z])([A-Z])/g, '$1_$2')
          .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
          .toLowerCase()
        mapped[autoKey] = data[key]
      }
    }
  }

  // DB `name` column is NOT NULL and separate from `venue_name`. Form's `name`
  // routes to `venue_name` (public pages read that), but the legacy `name`
  // column still needs a value or the insert/update fails. Mirror the value.
  if (mapped.venue_name !== undefined && mapped.venue_name !== null && mapped.venue_name !== '') {
    mapped.name = mapped.venue_name
  }

  // assigned_categories is TEXT[], serialize object array down to slug strings.
  // The form stores objects {id, slug, name, ...} in memory; the DB only stores slugs.
  if (Array.isArray(mapped.assigned_categories)) {
    mapped.assigned_categories = mapped.assigned_categories
      .map((c: any) => (typeof c === 'string' ? c : (c?.slug || c?.id || '')))
      .filter(Boolean)
  }

  return mapped
}

/**
 * Sanitize listing payload before sending to Supabase
 * Converts empty strings to null for numeric/date fields
 * Converts valid values to appropriate types
 */
function sanitizeListingPayload(data: any): any {
  const sanitized = { ...data }

  // Integer fields that should be null if empty
  const integerFields = [
    'capacity_min',
    'capacity_max',
    'built_year',
    'review_count',
    'enquiries',
    'view_count',
  ]

  // Numeric/decimal fields that should be null if empty
  const numericFields = [
    'lat',
    'lng',
    'property_size',
    'rating',
    'lwd_score',
  ]

  // Date fields that should be null if empty
  const dateFields = [
    'feature_start_date',
    'feature_end_date',
    'published_at',
  ]

  // Process integer fields
  for (const field of integerFields) {
    if (field in sanitized) {
      const value = sanitized[field]
      if (value === '' || value === undefined || value === null) {
        sanitized[field] = null
      } else {
        const num = parseInt(String(value), 10)
        sanitized[field] = isNaN(num) ? null : num
      }
    }
  }

  // Process numeric/decimal fields
  for (const field of numericFields) {
    if (field in sanitized) {
      const value = sanitized[field]
      if (value === '' || value === undefined || value === null) {
        sanitized[field] = null
      } else {
        const num = parseFloat(String(value))
        sanitized[field] = isNaN(num) ? null : num
      }
    }
  }

  // Process date fields
  for (const field of dateFields) {
    if (field in sanitized) {
      const value = sanitized[field]
      if (value === '' || value === undefined || value === null) {
        sanitized[field] = null
      }
      // Keep valid date strings as-is
    }
  }

  return sanitized
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════

export interface ListingMedia {
  id: string
  listing_id: string
  type: 'image' | 'video'
  url: string
  caption?: string
  alt_text?: string
  role?: 'hero' | 'card' | 'gallery'
  featured: boolean
  display_order: number
  video_title?: string
  video_type?: 'vimeo' | 'youtube' | 'internal'
  thumbnail?: string
  duration?: string
}

export interface Listing {
  id?: string
  listingType: string
  tier: string
  name: string
  slug: string
  description?: string
  shortDescription?: string
  country?: string
  countrySlug?: string
  region?: string
  regionSlug?: string
  city?: string
  citySlug?: string
  address?: string
  postcode?: string
  lat?: number
  lng?: number
  nearestAirport?: string
  travelTime?: string
  categorySlug?: string
  subCategorySlug?: string
  tags?: string[]
  styles?: string[]
  cardTitle?: string
  cardSubtitle?: string
  cardSummary?: string
  cardImage?: string
  cardBadge?: string
  cardStyle?: string
  cardFeatured?: boolean
  cardPromoted?: boolean
  heroType?: string
  heroEyebrow?: string
  heroTitle?: string
  heroSubtitle?: string
  heroImage?: string
  heroImageSet?: string[]
  heroVideoUrl?: string
  heroYoutubeUrl?: string
  heroVimeoUrl?: string
  heroPosterImage?: string
  heroCTA?: string
  heroBadge?: string
  capacityMin?: number
  capacityMax?: number
  builtYear?: number
  propertySize?: number
  propertySizeUnit?: string
  rating?: number
  reviewCount?: number
  priceLabel?: string
  priceFrom?: string
  priceCurrency?: string
  venueType?: string
  ceremonySpaces?: string
  receptionSpaces?: string
  accommodation?: string
  dining?: string
  amenities?: string[]
  exclusiveUse?: boolean
  indoorOption?: boolean
  outdoorOption?: boolean
  fireworksPolicy?: string
  dronePolicy?: string
  accessibilityNotes?: string
  heroMediaRole?: string
  cardMediaRole?: string
  galleryMediaRole?: string
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
  focusKeyword?: string
  slugPreview?: string
  internalNotes?: string
  status?: 'draft' | 'published' | 'paused' | 'archived'
  isFeatured?: boolean
  isPromoted?: boolean
  isHidden?: boolean
  isVerified?: boolean
  lwdScore?: number
  featureStartDate?: string
  featureEndDate?: string
  enquiries?: number
  viewCount?: number
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
  // Editorial Content Layer (Phase 3)
  heroSummary?: string
  sectionIntros?: Record<string, string>
  editorialApproved?: boolean
  editorialFactChecked?: boolean
  editorialLastReviewedAt?: string
  editorialLastReviewedBy?: string
  refreshNotes?: string
  contentQualityScore?: number
}

// ═══════════════════════════════════════════════════════════════════════════
// LISTINGS SERVICE
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// AI MEDIA INDEX SYNC
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fire-and-forget: sync a listing's media_items into the media_ai_index table.
 *
 * Called after every successful create / update. Non-blocking, failures are
 * logged as warnings but never throw or affect the save result.
 *
 * @param listingId  , the saved listing's uuid
 * @param data       , the raw form payload (contains mediaItems + meta fields)
 */
function syncMediaAIIndex(listingId: string, data: any): void {
  if (!listingId) return;

  const mediaItems = Array.isArray(data.mediaItems)
    ? data.mediaItems
    : [];

  const listingMeta = {
    id:          listingId,
    name:        data.name        || data.venueName     || '',
    category:    data.categorySlug                      || '',
    country:     data.country                           || '',
    region:      data.region                            || '',
    destination: data.countrySlug || data.destination   || '',
    type:        data.listingType                       || 'venue',
  };

  // Fire-and-forget, do not await
  supabase!.functions
    .invoke('sync-media-ai-index', {
      body: { listing_id: listingId, listing_meta: listingMeta, media_items: mediaItems },
    })
    .then(({ error }) => {
      if (error) {
        console.warn('[AI sync] media_ai_index sync failed (non-blocking):', error);
      }
    })
    .catch((err) =>
      console.warn('[AI sync] media_ai_index invoke error (non-blocking):', err)
    );
}

/**
 * Create a new listing in Supabase
 */
export async function createListing(data: Listing) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  track('save_started', { entity: 'listing' })
  try {
    // Ensure a valid token before writing so the insert can't hang on an inline refresh.
    await ensureSessionReady()

    // Prepare data (auto-generate slug, ensure required fields)
    const prepared = prepareListingData(data)

    // Map form field names to database field names
    const mapped = mapFormToDatabaseFields(prepared)

    // Sanitize payload: convert empty strings to null for numeric/date fields
    const dbData = sanitizeListingPayload(mapped)

    // Direct write, admin RLS policy allows this from the authenticated client.
    // No edge function, no cold start, no auth round-trip inside a Deno function.
    // abortSignal: fail fast on a hung token refresh instead of an infinite stall.
    const { data: listing, error } = await supabase!
      .from('listings')
      .insert([dbData])
      .abortSignal(AbortSignal.timeout(WRITE_TIMEOUT_MS))
      .select()
      .single()

    if (error) {
      if (error.message?.toLowerCase().includes('abort')) throw writeTimeoutError()
      // Column-not-found: strip missing column and retry once
      const missing = extractMissingColumn(error.message)
      if (missing && missing in dbData) {
        const stripped = { ...dbData }
        delete stripped[missing]
        const { data: listing2, error: err2 } = await supabase!
          .from('listings')
          .insert([stripped])
          .abortSignal(AbortSignal.timeout(WRITE_TIMEOUT_MS))
          .select()
          .single()
        if (err2) throw new Error(err2.message)
        if (!listing2) throw new Error('Save not verified, the insert returned no record')
        syncMediaAIIndex(listing2.id, data)
        return await finishVerifiedListingSave(listing2.id, listing2)
      }
      throw new Error(error.message)
    }

    if (!listing) throw new Error('Save not verified, the insert returned no record')

    // Fire-and-forget: sync rich media metadata into AI search index
    syncMediaAIIndex(listing.id, data)

    return await finishVerifiedListingSave(listing.id, listing)
  } catch (error) {
    track('save_failed', {
      entity: 'listing',
      message: error instanceof Error ? error.message : String(error),
    })
    console.error('Error creating listing:', error)
    throw error
  }
}

/**
 * Update an existing listing
 */
// Verified-save read-back: a fresh SELECT through RLS with the active session.
// Covered for every legitimate writer (admin via listings_admin_all, vendor via
// listings_vendor_select_own, drafts included). Throws when the row is not
// visible, a save that cannot be read back is a failed save, not a success.
// Shared tail of every successful update path: write confirmed → read back →
// telemetry → camelCase row for the caller.
async function finishVerifiedListingSave(id: string, row: any) {
  track('save_write_success', { entity: 'listing', entity_id: id })
  await confirmListingReadable(id)
  track('save_readback_success', { entity: 'listing', entity_id: id })
  return snakeToCamel(row) as Listing
}

async function confirmListingReadable(id: string) {
  // Time-boxed like the writes: a stalled read-back must fail fast, never hang.
  const { data, error } = await supabase!
    .from('listings')
    .select('id, updated_at')
    .eq('id', id)
    .abortSignal(AbortSignal.timeout(READBACK_TIMEOUT_MS))
    .maybeSingle()
  if (error) {
    if (error.message?.toLowerCase().includes('abort')) {
      throw new Error('Save timed out confirming the change. Please try again.')
    }
    throw new Error(`Save not verified, read-back failed: ${error.message}`)
  }
  if (!data) throw new Error('Save not verified, the listing could not be read back after the update')
  return data
}

export async function updateListing(id: string, data: Partial<Listing>) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  track('save_started', { entity: 'listing', entity_id: id })
  try {
    // Ensure a valid token before writing so the update can't hang on an inline refresh.
    await ensureSessionReady()

    // Extract optimistic lock sentinel before data preparation, it is not a real DB column
    // and must never reach mapFormToDatabaseFields or the Supabase payload.
    const expectedPublishVersion = (data as any).expectedPublishVersion as number | undefined
    const dataWithoutLock = { ...data }
    delete (dataWithoutLock as any).expectedPublishVersion

    // Prepare data (auto-generate slug if needed)
    const prepared = prepareListingData({ ...dataWithoutLock } as Listing)

    // Map form field names to database field names.
    // Include publish_version increment when an expected version is provided.
    const mapped = mapFormToDatabaseFields({
      ...prepared,
      updatedAt: new Date().toISOString(),
      ...(expectedPublishVersion !== undefined && { publishVersion: expectedPublishVersion + 1 }),
    })

    // Sanitize payload: convert empty strings to null for numeric/date fields
    const updateData = sanitizeListingPayload(mapped)

    // Log the admin JWT claim for diagnostics, but NON-BLOCKING. getSession()
    // can trigger a hanging token refresh on a stale token; never await it on
    // the save path (that was a primary cause of saves stalling). Fire and forget.
    supabase!.auth.getSession()
      .then(({ data: sessionData }) => {
        const isAdmin = sessionData.session?.user?.app_metadata?.is_admin
        console.log('[listings] admin claim:', isAdmin, '| listing id:', id)
      })
      .catch(() => {})

    // ── Optimistic lock: publish path ────────────────────────────────────────
    // When expectedPublishVersion is supplied the caller is publishing (not a
    // draft save). Only succeed if publish_version in the DB still matches,
    // prevents two open tabs from silently overwriting each other.
    const isPublish = updateData.status === 'published' && expectedPublishVersion !== undefined
    if (isPublish) {
      const { data: locked, error: lockErr } = await supabase!
        .from('listings')
        .update({ ...updateData, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('publish_version', expectedPublishVersion)
        .abortSignal(AbortSignal.timeout(WRITE_TIMEOUT_MS))
        .select()
        .maybeSingle()

      if (lockErr) {
        if (lockErr.message?.toLowerCase().includes('abort')) throw writeTimeoutError()
        const missing = extractMissingColumn(lockErr.message)
        if (missing && missing in updateData) {
          console.warn(`[listings] Column "${missing}" not in DB, stripping and retrying. Apply the pending migration.`)
          const stripped = { ...updateData }
          delete stripped[missing]
          const { data: locked2, error: err2 } = await supabase!
            .from('listings')
            .update({ ...stripped, updated_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .maybeSingle()
          if (err2) throw new Error(err2.message)
          if (!locked2) throw new Error('Publish conflict, this listing was published in another tab while yours was running. Refresh and try again.')
          syncMediaAIIndex(id, data)
          return await finishVerifiedListingSave(id, locked2)
        }
        throw new Error(lockErr.message)
      }
      if (!locked) {
        throw new Error('Publish conflict, this listing was published in another tab while yours was running. Refresh and try again.')
      }
      syncMediaAIIndex(id, data)
      return await finishVerifiedListingSave(id, locked)
    }

    // ── Draft saves and unlocked updates ─────────────────────────────────────
    // Direct write, admin RLS policy allows this from the authenticated client.
    // abortSignal: fail fast on a hung token refresh instead of an infinite stall.
    let { data: listing, error } = await supabase!
      .from('listings')
      .update({ ...updateData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .abortSignal(AbortSignal.timeout(WRITE_TIMEOUT_MS))
      .select()
      .single()

    if (error) {
      if (error.message?.toLowerCase().includes('abort')) throw writeTimeoutError()
      // Column-not-found: strip missing column and retry once (migration not yet applied)
      const missing = extractMissingColumn(error.message)
      if (missing && missing in updateData) {
        console.warn(`[listings] Column "${missing}" not in DB, stripping and retrying. Apply the pending migration.`)
        const stripped = { ...updateData }
        delete stripped[missing]
        const { data: listing2, error: err2 } = await supabase!
          .from('listings')
          .update({ ...stripped, updated_at: new Date().toISOString() })
          .eq('id', id)
          .abortSignal(AbortSignal.timeout(WRITE_TIMEOUT_MS))
          .select()
          .single()
        if (err2) throw new Error(err2.message)
        if (!listing2) throw new Error('Save not verified, the update returned no record')
        syncMediaAIIndex(id, data)
        return await finishVerifiedListingSave(id, listing2)
      }
      throw new Error(error.message)
    }

    if (!listing) throw new Error('Save not verified, the update returned no record')

    // Fire-and-forget: sync rich media metadata into AI search index
    syncMediaAIIndex(id, data)

    return await finishVerifiedListingSave(id, listing)
  } catch (error) {
    track('save_failed', {
      entity: 'listing', entity_id: id,
      message: error instanceof Error ? error.message : String(error),
    })
    console.error('Error updating listing:', error)
    throw error
  }
}

/**
 * Fetch a single listing by ID.
 * Routes through the admin-listings edge function (service role) so RLS is
 * bypassed and both admin and vendor loads work without policy changes.
 */
export async function fetchListingById(id: string) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const listing = await callListingsEdge({ action: 'getById', id })
    if (!listing) throw new Error(`Listing not found (id: ${id})`)
    return mapListingFromDb(listing)
  } catch (error) {
    console.error('Error fetching listing:', error)
    throw error
  }
}

/**
 * Fetch the listing linked to a managed account.
 * Routes through the edge function so vendor portal loads bypass RLS.
 */
export async function fetchListingByManagedAccountId(managedAccountId: string) {
  if (!isSupabaseAvailable()) return null;
  try {
    const listing = await callListingsEdge({ action: 'getByVendorAccountId', vendorAccountId: managedAccountId })
    if (!listing) return null;
    return mapListingFromDb(listing);
  } catch (err) {
    console.error('[Listings] fetchListingByManagedAccountId error:', err);
    return null;
  }
}

/**
 * Fetch a single listing by slug
 */
// Public-safe: only returns published listings. Draft listings must not render
// at public URLs for any user type. Admin preview uses fetchListingById.
export async function fetchListingBySlug(slug: string) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const { data: listing, error } = await supabase!
      .from('listings')
      .select()
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle()

    if (error) throw error
    if (!listing) return null
    const mapped = mapListingFromDb(listing)
    ;(mapped as any).accommodationTypes = await fetchAccommodationTypes((listing as any).id)
    // Project 3 (404 investigation, Class 4): showcase_enabled lives on the LISTING and
    // the showcase's own publish status lives on venue_showcases — they can disagree
    // (six-senses-southern-dunes: listing published, showcase_enabled true, showcase
    // itself still draft). VenueProfile.jsx's "View Showcase" link must not advertise a
    // showcase that isn't actually live; check the real status here, once, rather than
    // trusting the flag.
    if ((mapped as any).showcaseEnabled) {
      const { data: sc } = await supabase!.from('venue_showcases').select('status').eq('slug', slug).maybeSingle()
      ;(mapped as any).showcasePublished = sc?.status === 'published'
    }
    return mapped
  } catch (error) {
    throw error
  }
}

// Public read of accommodation entities (RLS allows anon SELECT of active rows).
export async function fetchAccommodationTypes(listingId: string) {
  if (!isSupabaseAvailable() || !listingId) return []
  try {
    const { data, error } = await supabase!
      .from('listing_accommodation_types')
      .select('*, listing_accommodation_type_media(media_item_id, image_url, position)')
      .eq('listing_id', listingId)
      .eq('status', 'active')
      .order('position', { ascending: true })
    if (error) return []
    return data || []
  } catch {
    return []
  }
}

// Token-gated fetch for private sales previews: renders a DRAFT listing's profile when
// opened from a valid /preview/<token> link, without publishing it. Backed by the
// SECURITY DEFINER RPC get_listing_for_preview (returns nothing for an invalid token).
export async function fetchListingForPreview(slug: string, token: string) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not configured')
  if (!token) return null
  const { data, error } = await supabase!.rpc('get_listing_for_preview', { p_token: token, p_slug: slug })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row) return null
  return mapListingFromDb(row)
}

/**
 * Fetch all listings with optional filters
 */
export async function fetchListings(filters?: {
  status?: string
  category_slug?: string
  region_slug?: string
  country_slug?: string
  listing_type?: string
}) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    // Route through edge function (service role), bypasses RLS, works for all statuses
    const result = await callListingsEdge({ action: 'list', filters: filters ?? {} })

    // result is the raw array of snake_case rows from the edge function
    const rows: any[] = Array.isArray(result) ? result : []

    // Convert snake_case database fields back to camelCase for frontend
    const camelListings = rows.map(listing => snakeToCamel(listing))

    // Transform to match UI expectations (status formatting, display fields, etc.)
    const uiListings = camelListings.map(listing => transformSupabaseListingForUI(listing))

    return uiListings as Listing[]
  } catch (error) {
    console.error('Error fetching listings:', error)
    throw error
  }
}

/**
 * Fetch ALL listings for admin panels, direct Supabase query, no edge function.
 * Uses the authenticated admin session so RLS grants access to all rows.
 * Much faster than the edge function (no cold start).
 */
export async function fetchAllListingsDirect(filters?: {
  status?: string
  category_slug?: string
}) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not configured')
  // Select only the columns the admin listings table renders/filters on.
  // Full select(*) pulls ~55 KB/row (hero images, JSONB fields), 1.2 MB for 22
  // rows. The listing editor fetches the full record when a listing is opened.
  const COLS = [
    'id','slug','name','status','tier',
    'category','category_slug',
    'country','country_slug','region','region_slug',
    'is_featured','listing_type',
    // lightweight boolean, needed so the Virtual Try-On chip can render on cards
    'bridal_virtual_tryon',
    'vendor_account_id','managed_account_id',
    'updated_at','created_at',
    // hero_image is a single lightweight URL (varchar), enough for the list
    // thumbnail without pulling the heavy hero_images/media_items JSONB payloads.
    'hero_image',
  ].join(',')
  let q = supabase!.from('listings').select(COLS)
  if (filters?.status) q = q.eq('status', filters.status)
  if (filters?.category_slug) q = q.eq('category_slug', filters.category_slug)
  q = (q as any)
    .order('is_featured',  { ascending: false, nullsFirst: false })
    .order('updated_at',   { ascending: false, nullsFirst: false })
    .order('created_at',   { ascending: false })
  const { data, error } = await q
  if (error) throw error
  const camel = (data ?? []).map((r: any) => snakeToCamel(r))
  return camel.map((r: any) => transformSupabaseListingForUI(r)) as Listing[]
}

/**
 * Fetch published listings for public pages (homepage, directory cards).
 * Queries Supabase directly with the anon key, no admin auth required.
 * RLS policy "listings_public_select_published" allows anon SELECT on status='published'.
 */
// Columns a listing CARD needs, the union of what transformListing() and the card components
// read. Pass this as `columns` for card grids (homepage, etc.) instead of select('*'). It drops
// the fields that bloat a full row but are never rendered on a card: the `embedding` vector
// (~19 KB/row), hero_image_set, hero_images, rooms_images, dining_menu_images, faq_*, photo_*,
// ent_* arrays, contact_profile, and the hundreds of vendor-category columns. Keeps media_items
// (thumbnails). Measured: ~10.9 MB → ~3.85 MB for ~190 published listings.
export const CARD_COLUMNS =
  'id,listing_type,tier,name,card_title,card_subtitle,card_summary,card_image,card_badge,card_style,' +
  'card_featured,card_promoted,slug,country,country_slug,region,region_slug,city,city_slug,category,' +
  'category_slug,sub_category_slug,tags,styles,specialties,amenities,short_description,description,summary,' +
  'price_from,price_from_numeric,price_currency,price_label,price_range,capacity_min,capacity_max,capacity,' +
  'rating,review_count,is_verified,is_featured,is_promoted,status,visibility,lwd_score,service_tier,' +
  'showcase_enabled,lat,lng,bridal_virtual_tryon,content_quality_score,editorial_approved,editorial_fact_checked,' +
  'hero_image,hero_video_url,videos,media_items,created_at,updated_at,published_at'

export async function fetchPublicListings(filters?: {
  category_slug?: string
  region_slug?: string
  country_slug?: string
  listing_type?: string
  vendorsOnly?: boolean
  limit?: number
  // Column projection. DEFAULTS TO the lean CARD_COLUMNS set (grid/card fields only).
  // ── Do not use the full listing projection for grids. It pulls heavy JSONB/media fields
  //    (media_items, hero_image_set, rooms_images, dining_menu_images, the embedding vector …),
  //    ~10.9 MB for ~190 listings, it once overwhelmed the DB (522) and slowed every list page.
  // For the rare case that genuinely needs the whole row, opt in EXPLICITLY with columns: '*'.
  // Heavy is opt-in, never the default.
  columns?: string
  // Default order is is_featured-first (curated placement). Pass 'recent' to order purely
  // by updated_at/published_at/created_at instead, for surfaces that want the latest
  // listings first regardless of featured status (e.g. the homepage).
  sort?: 'default' | 'recent'
}) {
  if (!isSupabaseAvailable()) throw new Error('Supabase not configured')

  // Public, session-free client, this fetches PUBLISHED listings, which by
  // definition must not depend on the visitor's auth state. Using the
  // session-locked `supabase` client here made this hang/silently fail for
  // signed-in visitors (same class of bug fixed on RegionCategoryPage.jsx).
  const buildQ = () => {
  let q = supabaseAnon!
    .from('listings')
    // Lean by default (see the columns note above); '*' is an explicit opt-in only.
    .select(filters?.columns || CARD_COLUMNS)
    .eq('status', 'published')

  // Canonical rule (see src/utils/listingClassification.js):
  // Venue slugs are 'wedding-venues', 'venues' (categories table canonical), and NULL (legacy).
  if (filters?.category_slug) {
    const VENUE_SLUGS = new Set(['wedding-venues', 'venues']);
    q = VENUE_SLUGS.has(filters.category_slug)
      ? (q as any).or('category_slug.eq.wedding-venues,category_slug.eq.venues,category_slug.is.null')
      : q.eq('category_slug', filters.category_slug)
  }
  if (filters?.region_slug)   q = q.eq('region_slug',   filters.region_slug)
  if (filters?.country_slug)  q = q.eq('country_slug',  filters.country_slug)
  if (filters?.listing_type)  q = q.eq('listing_type',  filters.listing_type)
  // vendorsOnly, everything that is NOT a venue (planner/photographer/florist/etc.).
  // Lets vendor surfaces fetch their own set instead of scraping the venue-heavy recent feed.
  if (filters?.vendorsOnly)   q = (q as any).neq('listing_type', 'venue')

  q = filters?.sort === 'recent'
    ? (q as any)
        .order('updated_at',   { ascending: false, nullsFirst: false })
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at',   { ascending: false })
    : (q as any)
        .order('is_featured',  { ascending: false, nullsFirst: false })
        .order('updated_at',   { ascending: false, nullsFirst: false })
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at',   { ascending: false })

  if (filters?.limit) q = q.limit(filters.limit)
    return q
  }

  // Retry on error/empty (shared data-layer fix): high-volume public fetches intermittently
  // fail or return empty under prerender concurrency and live burst load, which dropped
  // homepage/discovery content (e.g. the London showcase strip). Rebuild each attempt
  // (single-use builder); a genuinely-empty result exhausts the retries and returns empty.
  let data: any[] = []
  for (let attempt = 1; attempt <= 5; attempt++) {
    const { data: d, error } = await buildQ()
    if (error) { if (attempt === 5) throw error }
    else { data = d ?? []; if (data.length > 0) break }
    if (attempt < 5) await new Promise((r) => setTimeout(r, 300 * attempt + Math.random() * 250))
  }

  const camel = (data ?? []).map((r: any) => snakeToCamel(r))
  return camel.map((r: any) => transformSupabaseListingForUI(r)) as Listing[]
}

/**
 * Save a listing as draft (status = 'draft')
 */
export async function saveDraft(id: string | undefined, data: Listing) {
  const draftData = {
    ...data,
    status: 'draft' as const,
  }

  if (id) {
    return updateListing(id, draftData)
  } else {
    return createListing(draftData)
  }
}

/**
 * Publish a listing (status = 'published')
 */
export async function publishListing(id: string | undefined, data: Listing) {
  const publishData = {
    ...data,
    status: 'published' as const,
    published_at: new Date().toISOString(),
  }

  if (id) {
    return updateListing(id, publishData)
  } else {
    return createListing(publishData)
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// VENDOR-SCOPED LISTING ACCESS
// ═══════════════════════════════════════════════════════════════════════════
// These functions route through the vendor-listings edge function, which
// scopes every operation to the caller's own listing (via vendors.linked_listing_id)
// and strips admin-only fields from update payloads. Use these from any
// vendor-facing UI, never updateListing() / fetchListingById() (admin paths).

async function callVendorListingsEdge(payload: Record<string, unknown>): Promise<any> {
  if (!supabase) throw new Error('Supabase not configured, cannot reach vendor-listings edge function')
  const { data, error } = await supabase.functions.invoke('vendor-listings', { body: payload })
  if (error) throw new Error((error as any).message || String(error) || 'Edge function error')
  if (!data?.success) throw new Error(data?.error || 'Edge function error')
  return data.data
}

/**
 * Fetch the current vendor user's own listing.
 * Returns the camelCased listing object, or null if no listing is linked.
 */
export async function fetchMyVendorListing(): Promise<Listing | null> {
  if (!isSupabaseAvailable()) return null
  try {
    const listing = await callVendorListingsEdge({ action: 'get' })
    return listing ? mapListingFromDb(listing) : null
  } catch (err) {
    console.error('[Listings] fetchMyVendorListing error:', err)
    throw err
  }
}

/**
 * Vendor-scoped update of the current vendor's own listing.
 * The edge function strips admin-only fields automatically (tier, status,
 * is_featured, prices, etc.) and updates only the vendor's own listing.
 *
 * @returns { listing, stripped }, the updated listing plus a list of any
 *   protected fields that were rejected from the payload (for UI feedback).
 */
export async function vendorUpdateListing(
  data: Partial<Listing>,
): Promise<{ listing: Listing; stripped: string[] }> {
  if (!isSupabaseAvailable()) throw new Error('Supabase not configured')
  const prepared  = prepareListingData({ ...data } as Listing)
  const mapped    = mapFormToDatabaseFields(prepared)
  const sanitized = sanitizeListingPayload(mapped)
  const result    = await callVendorListingsEdge({ action: 'update', payload: sanitized })
  return {
    listing:  snakeToCamel(result.listing) as Listing,
    stripped: result.stripped ?? [],
  }
}

/**
 * Delete a listing
 */
export async function deleteListing(id: string) {
  try {
    await callListingsEdge({ action: 'delete', id })
  } catch (error) {
    console.error('Error deleting listing:', error)
    throw error
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// LISTING MEDIA SERVICE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Add media to a listing
 */
export async function addListingMedia(listingId: string, media: Omit<ListingMedia, 'id' | 'listing_id'>) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const { data, error } = await supabase!
      .from('listing_media')
      .insert([{ ...media, listing_id: listingId }])
      .select()
      .single()

    if (error) throw error
    return data as ListingMedia
  } catch (error) {
    console.error('Error adding listing media:', error)
    throw error
  }
}

/**
 * Fetch media for a listing
 */
export async function fetchListingMedia(listingId: string) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const { data, error } = await supabase!
      .from('listing_media')
      .select()
      .eq('listing_id', listingId)
      .order('display_order', { ascending: true })

    if (error) throw error
    return data as ListingMedia[]
  } catch (error) {
    console.error('Error fetching listing media:', error)
    return []
  }
}

/**
 * Update listing media
 */
export async function updateListingMedia(mediaId: string, updates: Partial<ListingMedia>) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const { data, error } = await supabase!
      .from('listing_media')
      .update(updates)
      .eq('id', mediaId)
      .select()
      .single()

    if (error) throw error
    return data as ListingMedia
  } catch (error) {
    console.error('Error updating listing media:', error)
    throw error
  }
}

/**
 * Delete listing media
 */
export async function deleteListingMedia(mediaId: string) {
  if (!isSupabaseAvailable()) {
    console.error('Supabase not configured')
    throw new Error('Supabase not configured')
  }

  try {
    const { error } = await supabase!
      .from('listing_media')
      .delete()
      .eq('id', mediaId)

    if (error) throw error
  } catch (error) {
    console.error('Error deleting listing media:', error)
    throw error
  }
}

// ── Similar Venues (taste engine surface) ──────────────────────────────────────
// Nearest neighbours of an anchor venue in embedding space, via the
// match_similar_venues RPC. Reuses the existing listing embeddings, content
// similarity works cold; behavioural similarity blends in later. One batched anon
// select keeps the rail cheap (no per-card edge round-trips).

export interface SimilarVenue {
  id: string
  name: string
  slug: string
  region: string | null
  country: string | null
  city: string | null
  countrySlug: string | null
  regionSlug: string | null
  categorySlug: string | null
  priceFrom: number | null
  priceCurrency: string | null
  heroImage: string | null
  similarity: number
  sameRegion: boolean
}

function pickSimilarHero(row: any): string | null {
  // Card-first: card_image is the purpose-built thumbnail. Then the first
  // *image* in media_items (skip videos, media_items[0] is frequently a
  // YouTube/Vimeo embed, which would render as a broken <img>). Then hero_image.
  if (row?.card_image && typeof row.card_image === 'string') return row.card_image
  const items = Array.isArray(row?.media_items) ? row.media_items : []
  for (const it of items) {
    const u = it?.url || it?.src || it?.image_url
    if (!u || typeof u !== 'string' || u.endsWith('/')) continue
    const isVideo =
      it?.type === 'video' ||
      it?.image_type === 'video' ||
      it?.source_type === 'youtube' ||
      it?.source_type === 'vimeo' ||
      /youtube\.com|youtu\.be|vimeo\.com/i.test(u)
    if (isVideo) continue
    return u
  }
  if (row?.hero_image && typeof row.hero_image === 'string') return row.hero_image
  if (Array.isArray(row?.hero_images) && row.hero_images[0]) return row.hero_images[0]
  return null
}

export async function fetchSimilarVenues(
  anchorId: string,
  count = 6,
): Promise<SimilarVenue[]> {
  if (!anchorId || !isSupabaseAvailable() || !supabase) return []

  try {
    const { data: matches, error: rpcErr } = await supabase.rpc('match_similar_venues', {
      anchor_id: anchorId,
      match_count: count,
    })
    if (rpcErr || !Array.isArray(matches) || matches.length === 0) return []

    // Total order on the RPC result: the SQL ranks by similarity DESC but ties are broken
    // nondeterministically, so the similar-venues carousel picked a different set/order per
    // render (prerender media drift, Project B). Re-rank client-side by similarity DESC with
    // a unique listing_id tiebreaker — preserves the ranking, makes the selection stable.
    const ordered = [...matches].sort((a: any, b: any) =>
      ((b.similarity ?? 0) - (a.similarity ?? 0)) || String(a.listing_id).localeCompare(String(b.listing_id)))
    const ids: string[] = ordered.map((m: any) => m.listing_id)
    const simById = new Map<string, { similarity: number; sameRegion: boolean }>(
      matches.map((m: any) => [m.listing_id, { similarity: m.similarity, sameRegion: m.same_region }]),
    )

    const { data: rows, error: rowsErr } = await supabase
      .from('listings')
      .select('id,name,slug,region,country,city,country_slug,region_slug,category_slug,price_from,price_currency,card_image,hero_image,hero_images,media_items')
      .in('id', ids)
      .eq('status', 'published')
    if (rowsErr || !Array.isArray(rows)) return []

    const byId = new Map<string, any>(rows.map((r: any) => [r.id, r]))
    // Preserve the RPC's similarity ordering (the .in() query does not guarantee it).
    return ids
      .map((id) => {
        const r = byId.get(id)
        if (!r) return null
        const meta = simById.get(id)!
        return {
          id: r.id,
          name: r.name,
          slug: r.slug,
          region: r.region ?? null,
          country: r.country ?? null,
          city: r.city ?? null,
          countrySlug: r.country_slug ?? null,
          regionSlug: r.region_slug ?? null,
          categorySlug: r.category_slug ?? null,
          priceFrom: r.price_from ?? null,
          priceCurrency: r.price_currency ?? null,
          heroImage: pickSimilarHero(r),
          similarity: meta.similarity,
          sameRegion: meta.sameRegion,
        } as SimilarVenue
      })
      .filter(Boolean) as SimilarVenue[]
  } catch {
    return []
  }
}

// Re-export the helper function
export { isSupabaseAvailable }
